/**
 * SaleDashboardPresenter
 * --
 */
import React, { useRef, useState, useEffect } from 'react';
import { Content, Title, Row, Col, Statistic } from 'components';
import { Button, Spin, Table } from 'antd';
import { stringToMoneyFormat } from 'utils';
import {
  XAxis,
  YAxis,
  BarChart,
  Bar,
  Tooltip,
  Legend,
  CartesianGrid,
  ComposedChart,
  Rectangle,
} from 'recharts';
import moment from 'moment';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  // CalendarOutlined,
  DownloadOutlined,
} from '@ant-design/icons';

// const COLORS = ['#2EBF6D', '#EFC563', '#E16070', '#2C97DE', '#675cb4'];
const styles = {
  title: {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: 20,
  },
  button: {
    width: 80,
    height: 40,
    fontSize: 15,
    fontWeight: 600,
    margin: '0px 4px',
  },
  form: {
    input: {
      large: {
        width: 175,
        marginRight: 8,
      },
      medium: {
        width: 100,
        marginRight: 8,
      },
    },
    button: {
      marginRight: 8,
    },
    date: {
      width: 120,
    },
    text: {
      fontSize: 13,
      fontWeight: 400,
    },
  },
  sales: {
    fontSize: 16,
    paddingLeft: 20,
    width: 150,
  },
};

/**
 * [Component] Presenter
 * --
 */
const SaleDashboardPresenter = ({
  today,
  isLoading,
  userList,
  orderList,
  orderListRaw,
  todayOrderList,
  totalPrice,
  chartData,
  discountPrice,
  refundPrice,
  onChangeDay,
  onDownloadExel,
  classificationTab,
  setClassificationTab,
  todayRankType,
  setTodayRankType,
}) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const chartRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);

  /* ===== VARIABLE ===== */

  /* ===== FUNCTION ===== */

  /* ===== HOOKS ===== */
  useEffect(() => {
    const call = (e) => {
      setChartWidth(chartRef.current.clientWidth);
    };

    if (chartRef.current) call();
    window.addEventListener('resize', call);
    return () => window.removeEventListener('resize', call);
  }, [chartRef]);

  /* ===== RENDER ===== */
  return (
    <>
      <Spin spinning={isLoading}>
        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          <Row>
            <Col x={12}>
              <Title style={{ ...styles.title }}>Today리포트</Title>
            </Col>
            <Col x={12} style={{ textAlign: 'right' }}>
              <Title
                level={5}
                align="right"
                style={{
                  ...styles.title,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
                subButton={
                  <span>
                    {/* <Button>
                      달력
                      <CalendarOutlined />
                    </Button>{' '} */}
                    <Button onClick={() => onChangeDay('today')}>오늘로</Button>
                  </span>
                }
              >
                <Button
                  shape="circle"
                  type="default"
                  size="small"
                  style={{ margin: '0 10px' }}
                  onClick={() => onChangeDay('prev')}
                >
                  <ArrowLeftOutlined />
                </Button>
                {today}

                <Button
                  shape="circle"
                  type="default"
                  size="small"
                  style={{ margin: '0 10px' }}
                  onClick={() => onChangeDay('next')}
                >
                  <ArrowRightOutlined />
                </Button>
              </Title>
            </Col>
            <Col x={24}>
              {/* 요약 */}
              <Content
                maxWidth={'100%'}
                padding={0}
                backgroundColor={'none'}
                style={{ marginBottom: 10, display: 'flex' }}
              >
                <div
                  style={{
                    background: '#FFFFFF',
                    borderRadius: 4,
                    width: `calc(100%)`,
                    marginRight: 0,
                    padding: 20,
                  }}
                >
                  <Title size={3} align="center">
                    <small>{today} 순 매출</small>{' '}
                    <span style={{ color: 'rgb(28, 107, 255)' }}>
                      {stringToMoneyFormat(totalPrice)}원
                    </span>
                  </Title>
                </div>
              </Content>
              <Content
                maxWidth={'100%'}
                padding={0}
                backgroundColor={'none'}
                style={{ marginBottom: 40, display: 'flex' }}
              >
                {[
                  {
                    title: '총 합계금액',
                    value: totalPrice - discountPrice,
                    unit: '원',
                  },
                  { title: '결제 건수', value: orderList.length, unit: '건' },
                  { title: '쿠폰/할인', value: discountPrice, unit: '원' },
                  { title: '주문회원 수', value: userList.total, unit: '명' },
                  { title: '취소/환불', value: refundPrice, unit: '원' },
                ].map((_item, _index) => (
                  <div
                    key={`${_item.label}-${_index}`}
                    style={{
                      background: '#FFFFFF',
                      borderRadius: 4,
                      width: `calc(100% / 5)`,
                      marginRight: _index === 4 ? 0 : 4,
                      padding: 20,
                    }}
                  >
                    <Statistic
                      title={_item.title}
                      value={stringToMoneyFormat(_item.value)}
                      suffix={_item.unit}
                      direction={'row'}
                      style={{ justifyContent: 'center' }}
                    />
                  </div>
                ))}
              </Content>
              {/* 요약 끝 */}

              <Row gutter={[20, 20]}>
                <Col x={24}>
                  <Title level={4} style={{ marginBottom: 7.5 }}>
                    오늘 상품 판매량 순위 Top10
                  </Title>
                  <Content maxWidth={'100%'} borderRadius={4}>
                    {[
                      { key: 'product_sort', name: '상품별' },
                      { key: 'platform_sort', name: '플랫폼별' },
                    ].map((item) => (
                      <>
                        <Button
                          key={`${item.key}`}
                          size="large"
                          // color="primary"
                          color={
                            item.key === todayRankType ? 'primary' : 'default'
                          }
                          variant={
                            item.key === todayRankType ? 'filled' : 'text'
                          }
                          onClick={() => setTodayRankType(item.key)}
                        >
                          {item.name}
                        </Button>
                      </>
                    ))}
                    <br />
                    <br />
                    <div ref={chartRef}>
                      {
                        <BarChart
                          width={chartWidth}
                          height={250}
                          data={chartData?.menuSales
                            ?.slice(0, 10)
                            .map((item) => ({
                              ...item,
                              // day: item.menu_name,
                              name: `${item.name.slice(0, 10)}`,
                              count: item?.num,
                              total: item?.total,
                              // price: 2400,
                              // amt: 45,
                            }))
                            .sort((a, b) =>
                              a.totalPrice < b.totalPrice
                                ? 1
                                : a.totalPrice > b.totalPrice
                                ? -1
                                : 0
                            )}
                        >
                          <XAxis dataKey="name" />
                          <YAxis
                            tickFormatter={(value) =>
                              stringToMoneyFormat(value)
                            }
                          />
                          <Bar
                            dataKey={
                              todayRankType === 'product_sort'
                                ? 'count'
                                : 'total'
                            }
                            fill="#82ca9d"
                            // fill={`rgb(55, 100, 101)`}
                            activeBar={
                              <Rectangle fill="gold" stroke="purple" />
                            }
                          />
                          <Tooltip
                            formatter={(value) => stringToMoneyFormat(value)}
                          />
                        </BarChart>
                      }
                    </div>
                  </Content>
                </Col>

                <Col x={12}>
                  <Title level={4} style={{ marginBottom: 7.5 }}>
                    분류별 매출
                  </Title>

                  <Content maxWidth={'100%'} borderRadius={4}>
                    <div>
                      {[
                        { key: 'daily', name: '일별 순매출' },
                        { key: 'weekly', name: '주별 순매출' },
                        // { key: 'monthly', name: '월별 순매출' },
                        { key: 'paymethod', name: '결제수단별 순매출' },
                      ].map((item) => (
                        <Button
                          key={`${item.key}`}
                          size="large"
                          color={
                            item.key === classificationTab
                              ? 'primary'
                              : 'default'
                          }
                          variant={
                            item.key === classificationTab ? 'filled' : 'text'
                          }
                          onClick={() => setClassificationTab(item.key)}
                        >
                          {item.name}
                        </Button>
                      ))}
                    </div>
                    <br />

                    <ComposedChart
                      layout="vertical"
                      width={685}
                      height={440}
                      data={chartData?.weeklyData}
                      margin={{
                        top: 20,
                        right: 20,
                        bottom: 20,
                        left: 20,
                      }}
                    >
                      <CartesianGrid stroke="#f5f5f5" />
                      <XAxis
                        type="number"
                        tickFormatter={(value) => stringToMoneyFormat(value)}
                      />
                      <YAxis dataKey="name" type="category" scale="band" />
                      <Tooltip
                        formatter={(value) => stringToMoneyFormat(value)}
                      />
                      <Legend />
                      <Bar dataKey="final_price" barSize={20} fill="#413ea0" />
                      <Bar
                        dataKey="final_discount"
                        barSize={20}
                        fill="#82ca9d"
                      />
                    </ComposedChart>
                  </Content>
                </Col>

                {/* === === */}
                <Col x={12}>
                  <Title
                    level={4}
                    style={{ marginBottom: 7.5 }}
                    subButton={
                      <Button onClick={onDownloadExel}>
                        <DownloadOutlined />
                        엑셀다운로드
                      </Button>
                    }
                  >
                    주문내역
                  </Title>
                  <Content
                    maxWidth={'100%'}
                    borderRadius={4}
                    style={{ minHeight: 526 }}
                  >
                    <Table
                      bordered
                      style={{ minHeight: 480 }}
                      dataSource={todayOrderList}
                      columns={[
                        {
                          title: '상품명',
                          dataIndex: 'name',
                          render: (_, item) => {
                            const menuLength = item.product_information.length;
                            const ml =
                              menuLength > 1 ? `외 ${menuLength - 1}건` : ``;
                            return `${item.product_information[0].menu_name} ${ml}`;
                          },
                          // render: (_, item) => JSON.stringify(item.menu),
                        },
                        {
                          title: '금액',
                          dataIndex: 'final_price',
                          render: (val) => stringToMoneyFormat(val),
                        },
                        {
                          title: '할인',
                          dataIndex: 'final_discount',
                          render: (val) => stringToMoneyFormat(val),
                        },
                        {
                          title: '주문시간',
                          dataIndex: 'created_at',
                          render: (val) =>
                            moment(val * 1000).format('YYYY-MM-DD HH:MM:ss'),
                        },
                        {
                          title: '처리',
                          dataIndex: 'payment_state',
                        },
                      ]}
                      size="small"
                    />
                  </Content>
                </Col>
                {/* === === */}
              </Row>
            </Col>
          </Row>
        </Content>
      </Spin>
    </>
  );
};

/* ===== DF ===== */
SaleDashboardPresenter.defaultProps = {
  isLoading: false,
  userList: {},
  orderList: [],
  todayOrderList: [],
  orderListRaw: [],
  chartData: {
    menuSales: [],
    weeklyData: [],
  },

  onDownloadExel: () => {},
};
export default SaleDashboardPresenter;
